import { CheckCircle2, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import { formulaToSegments, type AtomBalance } from './chemicalEquation';
import {
  FLAME_LABELS,
  PROTECTION_LABELS,
  type AirSupply,
  type FireAnswer,
  type FlameColour,
  type RustCase,
  type RustLevel,
} from './combustionCorrosion';

/** Formula with chemically correct subscripts, for example CO₂. */
export function Formula({ formula }: { formula: string }) {
  return (
    <span className="whitespace-nowrap">
      {formulaToSegments(formula).map((segment, index) =>
        segment.subscript ? <sub key={index}>{segment.text}</sub> : <span key={index}>{segment.text}</span>,
      )}
    </span>
  );
}

export function FeedbackPanel({
  correct,
  title,
  children,
}: {
  correct: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className={`mt-5 rounded-[1.5rem] border p-5 ${
        correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'
      }`}
    >
      <div className="flex items-start gap-3">
        {correct ? (
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <p className="font-extrabold text-ink">{title}</p>
          <div className="mt-2 space-y-2 font-medium leading-7 text-slate2">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Test tube with a nail (drawn from the case data)
// ---------------------------------------------------------------------------------------------

const TUBE = { left: 34, right: 86, top: 24, bottom: 222 } as const;
const LIQUID_TOP = 112;
const OIL_THICKNESS = 12;
const GRANULE_TOP = 196;
const PLUG_TOP = 184;
const NAIL = { x: 56, width: 8, wetTop: 64, wetBottom: 214, dryTop: 60, dryBottom: 178 } as const;
const RUST_SPOTS: Record<RustLevel, number> = { none: 0, some: 4, lots: 11 };
const GRANULE_COUNT = 9;
const SALT_DOT_COUNT = 10;

function rustSpotPositions(count: number, top: number, bottom: number) {
  return Array.from({ length: count }, (_, index) => {
    const y = top + ((index + 1) * (bottom - top)) / (count + 1);
    const x = NAIL.x + (index % 2 === 0 ? 1 : NAIL.width - 1);
    return { x, y, r: index % 3 === 0 ? 3.2 : 2.4 };
  });
}

function tubeDescription(entry: RustCase, level: RustLevel | null) {
  const parts = [PROTECTION_LABELS[entry.conditions.protection]];
  const { tube } = entry;
  if (tube.liquid === 'none') parts.push('no liquid');
  if (tube.liquid === 'tap-water') parts.push('half in tap water');
  if (tube.liquid === 'salt-water') parts.push('half in salt water');
  if (tube.liquid === 'boiled-water') parts.push('under boiled water');
  if (tube.liquid === 'boiled-salt-water') parts.push('under boiled salt water');
  if (tube.oilLayer) parts.push('oil layer on top');
  if (tube.dryingAgent) parts.push('drying agent at the bottom');
  parts.push(tube.stoppered ? 'stoppered' : 'open to the air');
  if (level) parts.push(`result: ${level === 'none' ? 'no rust' : level === 'some' ? 'a little rust' : 'lots of rust'}`);
  return `Test tube: ${parts.join(', ')}`;
}

export function RustTube({ entry, level }: { entry: RustCase; level: RustLevel | null }) {
  const { tube, conditions } = entry;
  const wet = tube.liquid !== 'none';
  const salty = tube.liquid === 'salt-water' || tube.liquid === 'boiled-salt-water';
  const nailTop = wet ? NAIL.wetTop : NAIL.dryTop;
  const nailBottom = wet ? NAIL.wetBottom : NAIL.dryBottom;
  const liquidTop = tube.oilLayer ? LIQUID_TOP - OIL_THICKNESS : LIQUID_TOP;
  const coating = conditions.protection;
  const coatingClass =
    coating === 'paint'
      ? 'stroke-brand-bubblegum'
      : coating === 'oil'
        ? 'stroke-brand-sunshine'
        : coating === 'galvanised' || coating === 'galvanised-scratched'
          ? 'stroke-slate2'
          : 'stroke-ink/40';
  const spots = level
    ? rustSpotPositions(RUST_SPOTS[level], wet ? LIQUID_TOP - 20 : nailTop + 10, nailBottom - 8)
    : [];

  return (
    <figure className="flex flex-col items-center">
      <svg viewBox="0 0 120 240" className="h-64 w-32" role="img" aria-label={tubeDescription(entry, level)}>
        {wet ? (
          <rect
            x={TUBE.left + 2}
            y={LIQUID_TOP}
            width={TUBE.right - TUBE.left - 4}
            height={TUBE.bottom - LIQUID_TOP - 4}
            rx="20"
            className="fill-wash-sky"
          />
        ) : null}
        {salty
          ? Array.from({ length: SALT_DOT_COUNT }, (_, index) => (
              <rect
                key={index}
                x={TUBE.left + 8 + ((index * 13) % 40)}
                y={LIQUID_TOP + 14 + index * 9}
                width="3"
                height="3"
                className="fill-slate2"
              />
            ))
          : null}
        {tube.oilLayer ? (
          <rect
            x={TUBE.left + 2}
            y={liquidTop}
            width={TUBE.right - TUBE.left - 4}
            height={OIL_THICKNESS}
            className="fill-brand-sunshine/60"
          />
        ) : null}
        {tube.dryingAgent ? (
          <>
            <rect x={TUBE.left + 2} y={PLUG_TOP} width={TUBE.right - TUBE.left - 4} height="10" className="fill-surface stroke-hairline" />
            {Array.from({ length: GRANULE_COUNT }, (_, index) => (
              <circle
                key={index}
                cx={TUBE.left + 9 + (index % 5) * 8}
                cy={GRANULE_TOP + 4 + Math.floor(index / 5) * 8}
                r="3.4"
                className="fill-stone2 stroke-slate2"
              />
            ))}
          </>
        ) : null}
        <rect x={NAIL.x - 5} y={nailTop - 6} width={NAIL.width + 10} height="6" rx="2" className="fill-slate2" />
        <rect
          x={NAIL.x}
          y={nailTop}
          width={NAIL.width}
          height={nailBottom - nailTop}
          className={`fill-stone2 ${coatingClass}`}
          strokeWidth={coating === 'bare' || coating === 'stainless' ? 1 : 3}
          strokeDasharray={coating === 'galvanised' || coating === 'galvanised-scratched' ? '4 2' : undefined}
        />
        {coating === 'galvanised-scratched' ? (
          <line x1={NAIL.x - 2} y1={nailTop + 70} x2={NAIL.x + NAIL.width + 2} y2={nailTop + 78} className="stroke-ink" strokeWidth="2" />
        ) : null}
        {coating === 'magnesium-strip'
          ? [0, 1, 2, 3].map((index) => (
              <line
                key={index}
                x1={NAIL.x - 3}
                y1={nailTop + 60 + index * 16}
                x2={NAIL.x + NAIL.width + 3}
                y2={nailTop + 68 + index * 16}
                className="stroke-slate2"
                strokeWidth="3"
              />
            ))
          : null}
        {spots.map((spot, index) => (
          <circle key={index} cx={spot.x} cy={spot.y} r={spot.r} className="fill-brand-coral" />
        ))}
        <path
          d={`M${TUBE.left} ${TUBE.top} V${TUBE.bottom - 26} a26 26 0 0 0 ${TUBE.right - TUBE.left} 0 V${TUBE.top}`}
          className="fill-none stroke-ink/40"
          strokeWidth="2.5"
        />
        {tube.stoppered ? (
          <rect x={TUBE.left - 4} y={TUBE.top - 14} width={TUBE.right - TUBE.left + 8} height="20" rx="4" className="fill-brand-coral/40 stroke-ink/30" />
        ) : null}
      </svg>
      <figcaption className="mt-2 max-w-[14rem] text-center text-xs font-bold leading-5 text-slate2">
        {PROTECTION_LABELS[conditions.protection]}
      </figcaption>
    </figure>
  );
}

export function ConditionChips({ entry }: { entry: RustCase }) {
  const chips = [
    { label: 'Water', present: entry.conditions.water },
    { label: 'Oxygen', present: entry.conditions.oxygen },
    { label: 'Salt', present: entry.conditions.salt },
  ];
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Conditions reaching the iron">
      {chips.map((chip) => (
        <li
          key={chip.label}
          className={`rounded-full border px-3 py-1 text-xs font-extrabold ${
            chip.present ? 'border-brand-sky/50 bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2 line-through'
          }`}
        >
          {chip.label}: {chip.present ? 'yes' : 'no'}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------
// Fire triangle
// ---------------------------------------------------------------------------------------------

const TRIANGLE_SIDES: { side: Exclude<FireAnswer, 'unsafe'>; x1: number; y1: number; x2: number; y2: number; lx: number; ly: number }[] = [
  { side: 'heat', x1: 30, y1: 160, x2: 110, y2: 22, lx: 32, ly: 84 },
  { side: 'oxygen', x1: 110, y1: 22, x2: 190, y2: 160, lx: 188, ly: 84 },
  { side: 'fuel', x1: 30, y1: 160, x2: 190, y2: 160, lx: 110, ly: 184 },
];

const SIDE_LABELS: Record<Exclude<FireAnswer, 'unsafe'>, string> = {
  heat: 'Heat',
  oxygen: 'Oxygen',
  fuel: 'Fuel',
};

export function FireTriangle({ removed }: { removed: FireAnswer | null }) {
  const label =
    removed === null
      ? 'Fire triangle with fuel, heat and oxygen all present'
      : removed === 'unsafe'
        ? 'Fire triangle: all three sides still present and the action adds danger'
        : `Fire triangle with the ${SIDE_LABELS[removed].toLowerCase()} side removed`;
  return (
    <svg viewBox="0 0 220 200" className="mx-auto w-full max-w-[16rem]" role="img" aria-label={label}>
      {TRIANGLE_SIDES.map((entry) => {
        const isRemoved = removed === entry.side;
        return (
          <g key={entry.side}>
            <line
              x1={entry.x1}
              y1={entry.y1}
              x2={entry.x2}
              y2={entry.y2}
              className={isRemoved ? 'stroke-brand-coral' : 'stroke-ink'}
              strokeWidth={isRemoved ? 3 : 6}
              strokeDasharray={isRemoved ? '6 6' : undefined}
              strokeLinecap="round"
            />
            <text
              x={entry.lx}
              y={entry.ly}
              textAnchor="middle"
              className={isRemoved ? 'fill-brand-coral text-[13px] font-extrabold' : 'fill-ink text-[13px] font-extrabold'}
            >
              {SIDE_LABELS[entry.side]}
              {isRemoved ? ' (removed)' : ''}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------------------------
// Flame and air supply
// ---------------------------------------------------------------------------------------------

const FLAME_CLASS: Record<FlameColour, string> = {
  blue: 'fill-brand-sky stroke-brand-sky',
  yellow: 'fill-brand-sunshine stroke-brand-coral',
  'not-visible': 'fill-none stroke-slate2',
};

export function FlameModel({ flame, airSupply }: { flame: FlameColour; airSupply: AirSupply }) {
  const airPercent = airSupply === 'plenty' ? 100 : 40;
  return (
    <div className="grid items-center gap-4 sm:grid-cols-[auto_minmax(0,1fr)]">
      <svg viewBox="0 0 80 120" className="mx-auto h-32 w-20" role="img" aria-label={FLAME_LABELS[flame]}>
        <path
          d="M40 10 C58 40 66 60 62 82 C58 104 22 104 18 82 C14 60 22 40 40 10 Z"
          className={FLAME_CLASS[flame]}
          strokeWidth="2"
          strokeDasharray={flame === 'not-visible' ? '4 4' : undefined}
        />
        <rect x="30" y="100" width="20" height="16" className="fill-slate2" />
      </svg>
      <div>
        <p className="text-sm font-extrabold text-ink">{FLAME_LABELS[flame]}</p>
        <p className="mt-2 text-xs font-bold text-slate2">Oxygen supply</p>
        <div
          className="mt-1 h-3 w-full rounded-full bg-surface"
          role="meter"
          aria-label="Oxygen supply"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={airPercent}
          aria-valuetext={airSupply === 'plenty' ? 'Plenty of oxygen' : 'Limited oxygen'}
        >
          <div
            className={`h-3 rounded-full ${airSupply === 'plenty' ? 'bg-brand-mint' : 'bg-brand-coral'}`}
            style={{ width: `${airPercent}%` }}
          />
        </div>
        <p className="mt-1 text-xs font-bold text-slate2">
          {airSupply === 'plenty' ? 'Plenty of oxygen' : 'Limited oxygen'}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Atom tally for equation balancing
// ---------------------------------------------------------------------------------------------

export function AtomTally({ atoms }: { atoms: AtomBalance[] }) {
  const max = Math.max(1, ...atoms.flatMap((atom) => [atom.left, atom.right]));
  return (
    <table className="mt-4 w-full text-left text-sm">
      <caption className="sr-only">Atom count on each side of the equation</caption>
      <thead>
        <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
          <th className="py-1 pr-2">Element</th>
          <th className="py-1 pr-2">Reactants</th>
          <th className="py-1 pr-2">Products</th>
          <th className="py-1">Match</th>
        </tr>
      </thead>
      <tbody>
        {atoms.map((atom) => (
          <tr key={atom.element} className="border-t border-hairline">
            <td className="py-2 pr-2 font-extrabold text-ink">{atom.element}</td>
            {[atom.left, atom.right].map((count, index) => (
              <td key={index} className="py-2 pr-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 font-extrabold text-ink">{count}</span>
                  <span className="hidden h-2 flex-1 rounded-full bg-surface sm:block">
                    <span
                      className={`block h-2 rounded-full ${atom.balanced ? 'bg-brand-mint' : 'bg-brand-coral'}`}
                      style={{ width: `${(count / max) * 100}%` }}
                    />
                  </span>
                </div>
              </td>
            ))}
            <td className="py-2 font-extrabold text-ink">{atom.balanced ? '✓ equal' : '✗ different'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
